import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import AutorenewIcon from '@mui/icons-material/Autorenew'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import {
    CandidateFolder,
    DriveFile,
    DriveFolder,
    DriveNode,
    KB,
    PineconeIndex,
    SyncStatus,
} from 'types/basesDeDados'

export type {
    SyncStatus,
    DocType,
    PendingAction,
    DriveFile,
    DriveFolder,
    DriveNode,
    KB,
    CandidateFolder,
    PineconeIndex,
} from 'types/basesDeDados'

export const SYNC_CONFIG: Record<
    SyncStatus,
    {color: string; wash: string; Icon: typeof CheckCircleOutlineIcon}
> = {
    ok: {color: '#0e8f78', wash: '#e7f8f3', Icon: CheckCircleOutlineIcon},
    run: {color: '#1769c4', wash: '#eaf3fd', Icon: AutorenewIcon},
    err: {color: '#cf3434', wash: '#fdecec', Icon: ErrorOutlineIcon},
}

const DIACRITICS_REGEX = /[\u0300-\u036f]/g

export function slugify(name: string) {
    return name
        .toLowerCase()
        .normalize('NFD')
        .replace(DIACRITICS_REGEX, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '')
}

// Vers\u00e3o "ao vivo" do slugify, usada enquanto o usu\u00e1rio digita: normaliza
// caracteres e espa\u00e7os para h\u00edfen, mas n\u00e3o corta h\u00edfen do in\u00edcio/fim, sen\u00e3o
// o usu\u00e1rio nunca conseguiria digitar um h\u00edfen no meio do texto.
export function slugifyLive(name: string): string {
    return name
        .toLowerCase()
        .normalize('NFD')
        .replace(DIACRITICS_REGEX, '')
        .replace(/[^a-z0-9-]+/g, '-')
        .replace(/-+/g, '-')
}

function folder(id: string, name: string, children: DriveNode[]): DriveFolder {
    return {id, kind: 'folder', name, children}
}

function file(
    id: string,
    name: string,
    type: DriveFile['type'],
    size: string,
    indexIds: string[] = []
): DriveFile {
    return {id, kind: 'file', name, type, size, indexIds}
}

export function countFiles(node: DriveNode): number {
    if (node.kind === 'file') return 1
    return node.children.reduce((sum, child) => sum + countFiles(child), 0)
}

export function findFolder(
    node: DriveNode,
    folderId: string
): DriveFolder | undefined {
    if (node.kind === 'file') return undefined
    if (node.id === folderId) return node
    for (const child of node.children) {
        const found = findFolder(child, folderId)
        if (found) return found
    }
    return undefined
}

export function collectPending(node: DriveNode): {
    uploads: DriveFile[]
    removals: DriveFile[]
} {
    if (node.kind === 'file') {
        return {
            uploads: node.pending === 'upload' ? [node] : [],
            removals: node.pending === 'remove' ? [node] : [],
        }
    }
    return node.children.reduce(
        (acc, child) => {
            const r = collectPending(child)
            return {
                uploads: [...acc.uploads, ...r.uploads],
                removals: [...acc.removals, ...r.removals],
            }
        },
        {uploads: [] as DriveFile[], removals: [] as DriveFile[]}
    )
}

// Aplica `updater` ao arquivo com o `id` indicado em qualquer nível da
// árvore. Se `updater` devolver null, o arquivo é removido da árvore.
export function mapFileInTree(
    node: DriveNode,
    id: string,
    updater: (file: DriveFile) => DriveFile | null
): DriveNode | null {
    if (node.kind === 'file') {
        return node.id === id ? updater(node) : node
    }
    const children = node.children
        .map((child) => mapFileInTree(child, id, updater))
        .filter((c): c is DriveNode => c !== null)
    return {...node, children}
}

// Adiciona arquivos dentro da pasta com o `folderId` indicado, em qualquer
// nível da árvore.
export function addFilesToFolder(
    node: DriveNode,
    folderId: string,
    newFiles: DriveFile[]
): DriveNode {
    if (node.kind === 'file') return node
    if (node.id === folderId) {
        return {...node, children: [...node.children, ...newFiles]}
    }
    return {
        ...node,
        children: node.children.map((child) =>
            addFilesToFolder(child, folderId, newFiles)
        ),
    }
}

// Confirma as pendências: remove arquivos marcados 'remove' e limpa o
// pending dos marcados 'upload'.
export function applyPendingToTree(node: DriveNode): DriveNode | null {
    if (node.kind === 'file') {
        if (node.pending === 'remove') return null
        return node.pending ? {...node, pending: undefined} : node
    }
    const children = node.children
        .map(applyPendingToTree)
        .filter((c): c is DriveNode => c !== null)
    return {...node, children}
}

// ponytail: "banco" mockado em memória até existir back de fato. As funções
// linkFolderToKb/addPineconeIndex mutam esses arrays/árvores diretamente —
// suficiente para navegar entre telas mantendo estado durante a sessão.
// Trocar por chamadas de API quando o back existir.
export const KBS: KB[] = [
    {
        name: 'jurisprudencia-tcu',
        folderName: 'TCU',
        drivePath: 'Drive / Bases / TCU',
        lastSync: 'ok',
        lastSyncLabel: 'Sincronizado',
        root: folder('r1', 'TCU', [
            file('d1', 'Sumula_vinculante.pdf', 'PDF', '1,1 MB', ['i1']),
            folder('r1-1', 'Acórdãos 2025', [
                file(
                    'd2',
                    'Acordao_1234_2025_Plenario.pdf',
                    'PDF',
                    '2,4 MB',
                    ['i1']
                ),
                file(
                    'd3',
                    'Parecer_juridico_dispensa.docx',
                    'DOCX',
                    '840 KB',
                    ['i2']
                ),
            ]),
        ]),
    },
    {
        name: 'editais-2026',
        folderName: 'Editais',
        drivePath: 'Drive / Bases / Editais',
        lastSync: 'run',
        lastSyncLabel: 'Sincronizando 64%',
        root: folder('r2', 'Editais', [
            file('d4', 'Edital_pregao_23.pdf', 'PDF', '5,8 MB', ['i3']),
            folder('r2-1', 'Planilhas', [
                file(
                    'd5',
                    'planilha_precos_referencia.xlsx',
                    'XLSX',
                    '560 KB',
                    ['i3']
                ),
            ]),
        ]),
    },
    {
        name: 'contratos-modelo',
        folderName: 'Contratos',
        drivePath: 'Drive / Bases / Contratos',
        lastSync: 'err',
        lastSyncLabel: 'Falha na sincronização',
        root: folder('r3', 'Contratos', [
            file('d6', 'Minuta_contrato_padrao.docx', 'DOCX', '1,2 MB', [
                'i4',
            ]),
            file('d7', 'notas_relatoria.txt', 'TXT', '32 KB'),
        ]),
    },
]

export const CANDIDATE_FOLDERS: CandidateFolder[] = [
    {
        id: 'f1',
        name: 'Pareceres',
        drivePath: 'Drive / Bases / Pareceres',
        fileCount: 47,
    },
    {
        id: 'f2',
        name: 'Acórdãos STJ',
        drivePath: 'Drive / Bases / STJ',
        fileCount: 214,
    },
    {
        id: 'f3',
        name: 'Manuais Internos',
        drivePath: 'Drive / Bases / Manuais',
        fileCount: 12,
    },
    {
        id: 'f4',
        name: 'Convênios',
        drivePath: 'Drive / Bases / Convenios',
        fileCount: 8,
    },
]

// Índices do Pinecone já gerados por cada KB (relação KB 1:N Índice),
// indexados pelo nome da KB.
export const PINECONE_INDEXES: Record<string, PineconeIndex[]> = {
    'jurisprudencia-tcu': [
        {
            id: 'i1',
            name: 'jurisprudencia-tcu-v2',
            date: '12/08/2026 14:32',
            status: 'ok',
            statusLabel: 'Concluído',
            uploaded: 3,
            removed: 1,
            triggeredBy: 'Carlos',
        },
        {
            id: 'i2',
            name: 'jurisprudencia-tcu-v1',
            date: '05/08/2026 09:10',
            status: 'ok',
            statusLabel: 'Concluído',
            uploaded: 8,
            removed: 0,
            triggeredBy: 'Carlos',
        },
    ],
    'editais-2026': [
        {
            id: 'i3',
            name: 'editais-2026-v1',
            date: '13/08/2026 08:02',
            status: 'run',
            statusLabel: 'Em andamento',
            uploaded: 0,
            removed: 0,
            triggeredBy: 'Carlos',
        },
    ],
    'contratos-modelo': [
        {
            id: 'i4',
            name: 'contratos-modelo-v1',
            date: '10/08/2026 17:45',
            status: 'err',
            statusLabel: 'Falhou',
            uploaded: 0,
            removed: 2,
            triggeredBy: 'Carlos',
        },
    ],
}

export function slugForFolder(folderName: string) {
    return slugify(folderName)
}

export function linkFolderToKb(
    folderId: string,
    customName?: string
): KB | undefined {
    const idx = CANDIDATE_FOLDERS.findIndex((f) => f.id === folderId)
    if (idx === -1) return undefined
    const [candidate] = CANDIDATE_FOLDERS.splice(idx, 1)
    const name = slugify(customName || candidate.name) || slugify(candidate.name)
    const kb: KB = {
        name,
        folderName: candidate.name,
        drivePath: candidate.drivePath,
        lastSync: 'ok',
        lastSyncLabel: 'Sincronizado',
        root: folder(`root-${candidate.id}`, candidate.name, []),
    }
    KBS.push(kb)
    return kb
}

// Gera o próximo nome de índice do Pinecone para essa KB (kb-v1, kb-v2, ...).
export function nextIndexName(kbName: string): string {
    const existing = PINECONE_INDEXES[kbName] ?? []
    return `${kbName}-v${existing.length + 1}`
}

export function addPineconeIndex(
    kbName: string,
    event: Omit<PineconeIndex, 'id' | 'name'>
): PineconeIndex[] {
    const list = PINECONE_INDEXES[kbName] ?? []
    const newIndex: PineconeIndex = {
        id: `i${Date.now()}`,
        name: nextIndexName(kbName),
        ...event,
    }
    const updated = [newIndex, ...list]
    PINECONE_INDEXES[kbName] = updated
    return updated
}

// Cria um índice vazio explicitamente (ação "+ Novo índice" dentro da KB) —
// os arquivos são associados a ele depois, um a um.
export function createIndex(kbName: string): PineconeIndex[] {
    return addPineconeIndex(kbName, {
        date: new Date().toLocaleString('pt-BR'),
        status: 'ok',
        statusLabel: 'Criado',
        uploaded: 0,
        removed: 0,
        triggeredBy: 'Você',
    })
}

// Percorre a árvore de documentos coletando os arquivos vinculados ao
// índice indicado.
export function filesForIndex(node: DriveNode, indexId: string): DriveFile[] {
    if (node.kind === 'file') {
        return node.indexIds.includes(indexId) ? [node] : []
    }
    return node.children.flatMap((child) => filesForIndex(child, indexId))
}

